import React, { useEffect, useState } from 'react';
import { FaFeather } from 'react-icons/fa';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface SiteStat {
  stat_name: string;
  stat_value: number;
}

const Hero: React.FC = () => {
  const contentRef = useScrollAnimation();
  const poemRef = useScrollAnimation();
  const [stats, setStats] = useState<Record<string, number>>({
    poems_created: 0,
    images_created: 0,
    poetry_readings: 0,
    music_created: 0,
    videos_created: 0
  });

  const fetchStats = async () => {
    try {
      // Fetch stats data
      const { data, error } = await supabase
        .from('site_stats')
        .select('stat_name, stat_value')
        .order('stat_name');
      
      if (error) {
        console.error('Error fetching site stats:', error.message);
        return;
      }

      if (!data || data.length === 0) {
        return;
      }

      const statsMap = (data as SiteStat[]).reduce((acc, stat) => {
        const value = typeof stat.stat_value === 'string' ? parseInt(stat.stat_value, 10) : stat.stat_value;
        return {
          ...acc,
          [stat.stat_name]: isNaN(value) ? 0 : value
        };
      }, {...stats});

      setStats(statsMap);
    } catch (err) {
      console.error('Failed to fetch site statistics:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('/images/backgrounds/hero-bg.jpg')] bg-cover bg-center transform scale-[1.02] 
                      transition-transform duration-[2s] filter brightness-75" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/40 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/30 to-purple-900/30" />
        <div className="absolute bottom-0 w-full h-64 bg-gradient-to-t from-black/80 to-transparent" />
      </div>

      {/* Content */}
      <div 
        ref={contentRef}
        className="relative z-10 text-center px-4 sm:px-6 md:px-8 w-full max-w-5xl mx-auto 
                 opacity-0 translate-y-8 duration-1000 ease-out"
      >
        {/* Icon */}
        <FaFeather className="mx-auto mb-8 text-white/80 w-8 h-8 animate-float" />
        
        {/* Main content */}
        <div className="space-y-12 sm:space-y-16">
          {/* Header section */}
          <div className="space-y-6">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold tracking-wide text-white
                        [text-shadow:_0_2px_4px_rgb(0_0_0_/_30%)]">
              Speak in Verse, See in Motion
            </h1>
            
            <div className="space-y-6">
              <div className="text-base sm:text-lg md:text-xl space-y-3 sm:space-y-4
                           font-serif text-white/90 tracking-wide max-w-3xl mx-auto">
                <p className="leading-relaxed">
                Let your words take shape, flow, and resonate through AI-crafted poetry visuals.
                </p>
                <p className="leading-relaxed text-white/75">
                  Create. Visualize. Inspire.
                </p>
              </div>

              {/* Featured Poem */}
              <div 
                ref={poemRef}
                className="relative max-w-2xl mx-auto px-6 py-8 rounded-2xl opacity-0 translate-y-8 delay-300
                         duration-1000 ease-out"
              >
                <div className="absolute inset-0 bg-gray-800/20 backdrop-blur-sm rounded-2xl" />
                <div className="relative">
                  {/* Top decorative line */}
                  <div className="flex items-center gap-4 mb-8">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
                    <div className="w-2 h-2 rounded-full bg-purple-500/50" />
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
                  </div>

                  {/* Poem content */}
                  <div className="space-y-3 my-6">
                    {[
                      "Through whispers of the morning breeze,",
                      "And sunsets that put hearts at ease.",
                      "Live your verse, let spirits soar,",
                      "With Parlando, create and explore."
                    ].map((line, index) => (
                      <p 
                        key={index}
                        className="italic text-gray-300 text-lg sm:text-xl leading-relaxed
                               hover:text-white/90 transition-colors duration-300"
                        style={{ 
                          transitionDelay: `${index * 100 + 500}ms`,
                          animation: `fadeIn 0.8s ease-out ${index * 100 + 500}ms both`
                        }}
                      >
                        {line}
                      </p>
                    ))}
                  </div>

                  {/* Bottom decorative line */}
                  <div className="flex items-center gap-4 mt-8">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
                    <div className="w-2 h-2 rounded-full bg-purple-500/50" />
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
                  </div>

                  {/* Optional: Add a subtle glow effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/0 via-purple-500/5 to-purple-500/0 
                               rounded-3xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-center max-w-lg mx-auto">
            <Link to="/dashboard">
              <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 text-white 
                              shadow-lg shadow-purple-500/20 hover:shadow-xl hover:shadow-purple-500/30 
                              hover:scale-[1.02] transition-all duration-300 min-w-[200px]
                              inline-flex items-center justify-center gap-2 group">
                <span className="group-hover:translate-x-1 transition-transform duration-300 
                             inline-flex items-center gap-2">
                  Start Creating 
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
                    <path d="M1 8h14M9 2l6 6-6 6" stroke="currentColor" strokeWidth="2" 
                          strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6 max-w-2xl mx-auto text-white/80">
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold mb-2">{stats.poems_created.toLocaleString()}+</div>
              <div className="text-sm text-white/60">Poems Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl font-bold mb-2">{stats.images_created.toLocaleString()}+</div>
              <div className="text-sm text-white/60">Images Created</div>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold mb-2">{stats.poetry_readings.toLocaleString()}+</div>
              <div className="text-sm text-white/60">Poetry Readings</div>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold mb-2">{stats.music_created.toLocaleString()}+</div>
              <div className="text-sm text-white/60">Music Created</div>
            </div>
            <div className="text-center col-span-2 sm:col-span-1">
              <div className="text-2xl sm:text-3xl font-bold mb-2">{stats.videos_created.toLocaleString()}+</div>
              <div className="text-sm text-white/60">Videos Created</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero; 